import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useAccountSites, isSiteOwner, useCurrentSite } from '@hotjar/site';
import { useProductCatalog } from '@hotjar/webapp/domains/common/product/hooks/useProductCatalog';
import { PostTrialModal, TrialsLegacyContext } from '@hotjar/trials';
import { useCurrentUser } from '@hotjar/user';
import { useShortLinkWidgetHiddenState } from '@hotjar/webapp/features/ShortLinkWidget';
import { useOwnedAccount, useAccountTrialInfo, TrialState } from '@hotjar/account';
import { usePostTrialLogging } from '@hotjar/webapp/hooks/usePostTrialLogging';
import { CHECKOUT } from '@hotjar/webapp/constants/routes';
import { getRouteConfigByPath } from '@hotjar/webapp/utils/routing';

/**
 * This component is used to fetch all the required data and decide whether the <PostTrialModal>
 * will be rendered or not.
 */
export const PostTrialModalContainer = (): JSX.Element | null => {
  const { data: account, isLoading: isLoadingAccount } = useOwnedAccount();
  const { data: currentSite } = useCurrentSite();

  const { setIsHidden: setIsShortLinkWidgetHidden, isHidden: initial } =
    useShortLinkWidgetHiddenState();

  const { data: currentUser } = useCurrentUser();

  const userId = currentUser?.id;

  const {
    query: { data: sites, isLoading: isLoadingSites },
  } = useAccountSites(account?.id);
  const { data: productCatalog, isLoading: isLoadingCatalog } = useProductCatalog();

  const trialInfo = useAccountTrialInfo();
  const { pathname } = useLocation();

  const trialExpired = trialInfo?.state === TrialState.EXPIRED;

  const isOwner = useMemo(() => isSiteOwner(userId, currentSite), [currentSite, userId]);

  const isLoading = isLoadingAccount || isLoadingSites || isLoadingCatalog;

  const isCheckoutPage = CHECKOUT.BASE.name === (getRouteConfigByPath(pathname)?.name || '');

  // We hide the shortLinkWidget if we are on expired trial because it interferes
  // with access to the post trial modal cta's. We reset it to its initial value
  // once this component is unmounted
  useEffect(() => {
    if (trialExpired) {
      setIsShortLinkWidgetHidden(true);
    }

    return () => {
      setIsShortLinkWidgetHidden(initial);
    };
  }, [initial, setIsShortLinkWidgetHidden, trialExpired]);

  /**
   * To open PostTrial modal
   * - can't be at the checkout page
   * - should be the Account Owner
   * - be in Trial Expired state
   */
  const shouldRenderPostTrialModal = useMemo(
    () => trialExpired && isOwner && !isCheckoutPage,
    [trialExpired, isOwner, isCheckoutPage],
  );

  usePostTrialLogging({
    shouldRenderPostTrialModal,
  });

  return shouldRenderPostTrialModal ? (
    <TrialsLegacyContext.Provider
      // FIXME: This creates a new object on every render and may create unnecessary rerenders.
      //        Fix this or add a comment to explain why it's desired.
      //        More details: https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/jsx-no-constructed-context-values.md
      // eslint-disable-next-line react/jsx-no-constructed-context-values
      value={{
        isLoading,
        sites,
        productCatalog,
        currentSite,
      }}
    >
      <PostTrialModal />
    </TrialsLegacyContext.Provider>
  ) : null;
};
